import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Plus, MoreHorizontal, Edit, Trash2, Loader2, Users, Shield, Building2, Eye, PenLine, Trash, Upload, Settings, LayoutDashboard, FileText, BarChart3, Plug, FormInput, Inbox, Bell, Mail, MessageSquare, CreditCard, UserPlus, Zap } from 'lucide-react';
import { toast } from 'sonner';
import CSVImportModal from '../components/CSVImportModal';
import CSVExportButton from '../components/CSVExportButton';

const TeamPage = () => {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [offices, setOffices] = useState([]);
  const [teams, setTeams] = useState([]);
  const [permissionsConfig, setPermissionsConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('roles');
  
  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    role_ids: [],
    office_id: '',
    team_id: '',
    no_team: false
  });
  
  // Role dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    navigation_permissions: [],
    module_permissions: {}
  });

  // Team dialog state
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamFormData, setTeamFormData] = useState({
    name: '',
    description: '',
    university_ids: [],
    admin_id: '',
    is_active: true,
    member_ids: []
  });

  // Universities for team assignment
  const [universities, setUniversities] = useState([]);
  const [unifiedAgents, setUnifiedAgents] = useState([]);
  const [assignAgentDialog, setAssignAgentDialog] = useState({ open: false, agent: null, team_id: '', counselor_id: '' });

  // Selection states
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, officesRes, teamsRes, configRes, universitiesRes, unifiedAgentsRes] = await Promise.all([
        api().get('/users?exclude_types=agent,student'),
        api().get('/roles'),
        api().get('/offices'),
        api().get('/teams'),
        api().get('/permissions/config'),
        api().get('/master-data?type=university'),
        api().get('/agents/unified').catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setOffices(officesRes.data);
      setTeams(teamsRes.data);
      setPermissionsConfig(configRes.data);
      setUniversities(universitiesRes.data || []);
      setUnifiedAgents(unifiedAgentsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // User CRUD
  const openCreateUser = () => {
    setEditingUser(null);
    const defaultOffice = offices.find(o => o.is_head_office);
    setUserFormData({
      name: '',
      email: '',
      password: '',
      role_ids: [],
      office_id: defaultOffice?.id || '',
      team_id: '',
      no_team: false
    });
    setUserDialogOpen(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      name: user.name,
      email: user.email,
      password: '',
      role_ids: user.role_ids || [],
      office_id: user.office_id || '',
      team_id: user.team_id || '',
      no_team: !user.team_id
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userFormData.name || !userFormData.email) {
      toast.error('Name and email are required');
      return;
    }
    if (!editingUser && !userFormData.password) {
      toast.error('Password is required for new users');
      return;
    }
    if (!userFormData.office_id) {
      toast.error('Office assignment is required');
      return;
    }
    if (userFormData.role_ids.length === 0) {
      toast.error('At least one role is required');
      return;
    }

    try {
      const data = { ...userFormData };
      if (!data.password) delete data.password;
      
      if (editingUser) {
        await api().put(`/users/${editingUser.id}`, data);
        toast.success('User updated successfully');
      } else {
        await api().post('/users', data);
        toast.success('User created successfully');
      }
      setUserDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.id === currentUser.id) {
      toast.error("You can't delete your own account");
      return;
    }
    if (!confirm(`Are you sure you want to delete "${user.name}"?`)) return;
    try {
      await api().delete(`/users/${user.id}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const toggleUserRole = (roleId) => {
    setUserFormData(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId]
    }));
  };

  // Selection handlers
  const handleSelectAllUsers = (checked) => {
    if (checked) {
      setSelectedUsers(users.map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} user(s)?`)) return;
    try {
      await Promise.all(selectedUsers.map(id => api().delete(`/users/${id}`)));
      toast.success(`${selectedUsers.length} user(s) deleted`);
      setSelectedUsers([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete some users');
    }
  };

  const handleSelectAllRoles = (checked) => {
    if (checked) {
      setSelectedRoles(roles.filter(r => !r.is_system).map(r => r.id));
    } else {
      setSelectedRoles([]);
    }
  };

  const handleSelectRole = (roleId, checked) => {
    if (checked) {
      setSelectedRoles(prev => [...prev, roleId]);
    } else {
      setSelectedRoles(prev => prev.filter(id => id !== roleId));
    }
  };

  const handleSelectAllTeams = (checked) => {
    if (checked) {
      setSelectedTeams(teams.map(t => t.id));
    } else {
      setSelectedTeams([]);
    }
  };

  const handleSelectTeam = (teamId, checked) => {
    if (checked) {
      setSelectedTeams(prev => [...prev, teamId]);
    } else {
      setSelectedTeams(prev => prev.filter(id => id !== teamId));
    }
  };

  const handleBulkDeleteTeams = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedTeams.length} team(s)?`)) return;
    try {
      await Promise.all(selectedTeams.map(id => api().delete(`/teams/${id}`)));
      toast.success(`${selectedTeams.length} team(s) deleted`);
      setSelectedTeams([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete some teams');
    }
  };

  // Role CRUD
  const openCreateRole = () => {
    setEditingRole(null);
    setRoleFormData({
      name: '',
      description: '',
      navigation_permissions: [],
      module_permissions: {}
    });
    setRoleDialogOpen(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleFormData({
      name: role.name,
      description: role.description || '',
      navigation_permissions: role.navigation_permissions || [],
      module_permissions: role.module_permissions || {}
    });
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleFormData.name) {
      toast.error('Role name is required');
      return;
    }

    try {
      if (editingRole) {
        await api().put(`/roles/${editingRole.id}`, roleFormData);
        toast.success('Role updated successfully');
      } else {
        await api().post('/roles', roleFormData);
        toast.success('Role created successfully');
      }
      setRoleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system) {
      toast.error('Cannot delete system roles');
      return;
    }
    if (!confirm(`Are you sure you want to delete "${role.name}"?`)) return;
    try {
      await api().delete(`/roles/${role.id}`);
      toast.success('Role deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const toggleNavPermission = (navKey) => {
    setRoleFormData(prev => ({
      ...prev,
      navigation_permissions: prev.navigation_permissions.includes(navKey)
        ? prev.navigation_permissions.filter(k => k !== navKey)
        : [...prev.navigation_permissions, navKey]
    }));
  };

  const toggleModulePermission = (moduleKey, permission) => {
    setRoleFormData(prev => {
      const currentPerms = prev.module_permissions[moduleKey] || [];
      const newPerms = currentPerms.includes(permission)
        ? currentPerms.filter(p => p !== permission)
        : [...currentPerms, permission];
      
      return {
        ...prev,
        module_permissions: {
          ...prev.module_permissions,
          [moduleKey]: newPerms
        }
      };
    });
  };

  const selectAllNavPermissions = () => {
    if (!permissionsConfig) return;
    setRoleFormData(prev => ({
      ...prev,
      navigation_permissions: permissionsConfig.navigation_items.map(n => n.key)
    }));
  };

  const clearAllNavPermissions = () => {
    setRoleFormData(prev => ({
      ...prev,
      navigation_permissions: []
    }));
  };

  const selectAllModulePermissions = () => {
    if (!permissionsConfig) return;
    const allPerms = {};
    permissionsConfig.modules.forEach(m => {
      allPerms[m.key] = [...m.permissions];
    });
    setRoleFormData(prev => ({
      ...prev,
      module_permissions: allPerms
    }));
  };

  const clearAllModulePermissions = () => {
    setRoleFormData(prev => ({
      ...prev,
      module_permissions: {}
    }));
  };

  // Team CRUD
  const openCreateTeam = () => {
    setEditingTeam(null);
    setTeamFormData({
      name: '',
      description: '',
      university_ids: [],
      admin_id: '',
      is_active: true,
      member_ids: []
    });
    setTeamDialogOpen(true);
  };

  const openEditTeam = (team) => {
    setEditingTeam(team);
    const currentMembers = users.filter(u => u.team_id === team.id).map(u => u.id);
    setTeamFormData({
      name: team.name,
      description: team.description || '',
      university_ids: team.university_ids || [],
      admin_id: team.admin_id || '',
      is_active: team.is_active !== false,
      member_ids: currentMembers
    });
    setTeamDialogOpen(true);
  };

  const toggleUniversitySelection = (universityId) => {
    setTeamFormData(prev => ({
      ...prev,
      university_ids: prev.university_ids.includes(universityId)
        ? prev.university_ids.filter(id => id !== universityId)
        : [...prev.university_ids, universityId]
    }));
  };

  const handleSaveTeam = async () => {
    if (!teamFormData.name) {
      toast.error('Team name is required');
      return;
    }
    try {
      const { member_ids, ...teamPayload } = teamFormData;
      let savedTeamId;
      if (editingTeam) {
        await api().put(`/teams/${editingTeam.id}`, teamPayload);
        savedTeamId = editingTeam.id;
        toast.success('Team updated successfully');
      } else {
        const res = await api().post('/teams', teamPayload);
        savedTeamId = res.data?.id;
        toast.success('Team created successfully');
      }

      // Sync members: users to add + users to remove (existing users only, no duplication)
      if (savedTeamId) {
        const prevMembers = editingTeam ? users.filter(u => u.team_id === editingTeam.id).map(u => u.id) : [];
        const toAdd = member_ids.filter(id => !prevMembers.includes(id));
        const toRemove = prevMembers.filter(id => !member_ids.includes(id));
        // Include the team admin as a member automatically
        if (teamPayload.admin_id && !toAdd.includes(teamPayload.admin_id) && !prevMembers.includes(teamPayload.admin_id)) {
          toAdd.push(teamPayload.admin_id);
        }
        await Promise.all([
          ...toAdd.map(uid => api().put(`/users/${uid}`, { team_id: savedTeamId })),
          ...toRemove.map(uid => api().put(`/users/${uid}`, { team_id: null })),
        ]);
      }

      setTeamDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save team');
    }
  };

  const handleDeleteTeam = async (team) => {
    if (!confirm(`Are you sure you want to delete "${team.name}"?`)) return;
    try {
      await api().delete(`/teams/${team.id}`);
      toast.success('Team deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  // Get icon component for nav item
  const getNavIcon = (iconName) => {
    const icons = {
      LayoutDashboard, Users, FileText, BarChart3, Plug, FormInput, Inbox,
      Building2, UserPlus, Bell, Settings, Mail, MessageSquare, CreditCard
    };
    return icons[iconName] || Settings;
  };

  // Get permission icon
  const getPermIcon = (perm) => {
    switch (perm) {
      case 'view': return Eye;
      case 'create': return Plus;
      case 'update': return PenLine;
      case 'delete': return Trash;
      case 'upload': return Upload;
      case 'use': return Zap;
      default: return Settings;
    }
  };

  // Pre-render user rows
  const userRows = [];
  for (let i = 0; i < users.length; i++) {
    const user = users[i];
    const userRoles = roles.filter(r => user.role_ids?.includes(r.id));
    const userOffice = offices.find(o => o.id === user.office_id);
    const userTeam = teams.find(t => t.id === user.team_id);
    
    userRows.push(
      <TableRow key={user.id}>
        <TableCell>
          <Checkbox 
            checked={selectedUsers.includes(user.id)}
            onCheckedChange={(checked) => handleSelectUser(user.id, checked)}
          />
        </TableCell>
        <TableCell>
          <div>
            <div className="font-medium">{user.name}</div>
            <div className="text-sm text-muted-foreground">{user.email}</div>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1">
            {userRoles.length > 0 ? userRoles.map(r => (
              <Badge key={r.id} variant="secondary">{r.name}</Badge>
            )) : <span className="text-muted-foreground">No roles</span>}
          </div>
        </TableCell>
        <TableCell>
          {userOffice ? (
            <div className="flex items-center gap-1">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              {userOffice.name}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell>
          {userTeam ? (
            <Badge variant="outline">{userTeam.name}</Badge>
          ) : (
            <span className="text-muted-foreground text-sm">No team</span>
          )}
        </TableCell>
        <TableCell>
          <Badge variant={user.is_active ? 'default' : 'secondary'}>
            {user.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </TableCell>
        <TableCell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openEditUser(user)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              {user.id !== currentUser.id && (
                <DropdownMenuItem onClick={() => handleDeleteUser(user)} className="text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    );
  }

  // Pre-render role rows
  const roleRows = [];
  for (let i = 0; i < roles.length; i++) {
    const role = roles[i];
    const navCount = role.navigation_permissions?.length || 0;
    const moduleCount = Object.keys(role.module_permissions || {}).length;
    
    roleRows.push(
      <TableRow key={role.id}>
        <TableCell>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{role.name}</span>
            {role.is_system && (
              <Badge variant="outline" className="text-xs">System</Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground text-sm">
          {role.description || '-'}
        </TableCell>
        <TableCell>
          {role.name === 'Admin' ? (
            <Badge variant="default">Full Access</Badge>
          ) : (
            <div className="flex gap-2">
              <Badge variant="secondary">{navCount} nav items</Badge>
              <Badge variant="secondary">{moduleCount} modules</Badge>
            </div>
          )}
        </TableCell>
        <TableCell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openEditRole(role)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              {!role.is_system && (
                <DropdownMenuItem onClick={() => handleDeleteRole(role)} className="text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    );
  }

  // Pre-render role checkboxes for user form
  const roleCheckboxes = [];
  for (let i = 0; i < roles.length; i++) {
    const role = roles[i];
    roleCheckboxes.push(
      <div key={role.id} className="flex items-center gap-2">
        <Checkbox
          id={`role-${role.id}`}
          checked={userFormData.role_ids.includes(role.id)}
          onCheckedChange={() => toggleUserRole(role.id)}
        />
        <Label htmlFor={`role-${role.id}`} className="text-sm font-normal cursor-pointer">
          {role.name}
        </Label>
      </div>
    );
  }

  // Pre-render office options
  const officeOptions = [];
  officeOptions.push(<SelectItem key="none" value="none">Select office...</SelectItem>);
  for (let i = 0; i < offices.length; i++) {
    const office = offices[i];
    officeOptions.push(
      <SelectItem key={office.id} value={office.id}>
        {office.name} {office.is_head_office ? '(Head Office)' : ''}
      </SelectItem>
    );
  }

  // Pre-render navigation permission checkboxes
  const navPermissionItems = [];
  if (permissionsConfig) {
    for (let i = 0; i < permissionsConfig.navigation_items.length; i++) {
      const nav = permissionsConfig.navigation_items[i];
      const Icon = getNavIcon(nav.icon);
      navPermissionItems.push(
        <div key={nav.key} className="flex items-center gap-2 p-2 rounded hover:bg-muted">
          <Checkbox
            id={`nav-${nav.key}`}
            checked={roleFormData.navigation_permissions.includes(nav.key)}
            onCheckedChange={() => toggleNavPermission(nav.key)}
          />
          <Icon className="w-4 h-4 text-muted-foreground" />
          <Label htmlFor={`nav-${nav.key}`} className="text-sm font-normal cursor-pointer flex-1">
            {nav.label}
          </Label>
        </div>
      );
    }
  }

  // Pre-render module permission sections
  const modulePermissionSections = [];
  if (permissionsConfig) {
    for (let i = 0; i < permissionsConfig.modules.length; i++) {
      const module = permissionsConfig.modules[i];
      const modulePerms = roleFormData.module_permissions[module.key] || [];
      
      const permCheckboxes = [];
      for (let j = 0; j < module.permissions.length; j++) {
        const perm = module.permissions[j];
        const PermIcon = getPermIcon(perm);
        permCheckboxes.push(
          <div key={perm} className="flex items-center gap-1.5">
            <Checkbox
              id={`${module.key}-${perm}`}
              checked={modulePerms.includes(perm)}
              onCheckedChange={() => toggleModulePermission(module.key, perm)}
            />
            <PermIcon className="w-3 h-3 text-muted-foreground" />
            <Label htmlFor={`${module.key}-${perm}`} className="text-xs font-normal cursor-pointer capitalize">
              {perm}
            </Label>
          </div>
        );
      }
      
      modulePermissionSections.push(
        <div key={module.key} className="p-3 border rounded-lg space-y-2">
          <div className="font-medium text-sm">{module.label}</div>
          <div className="flex flex-wrap gap-3">
            {permCheckboxes}
          </div>
        </div>
      );
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team Management</h1>
          <p className="text-muted-foreground">Manage users, roles, teams, and permissions</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="roles" className="gap-2" data-testid="tab-roles">
            <Shield className="w-4 h-4" />
            Roles ({roles.length})
          </TabsTrigger>
          <TabsTrigger value="policies" className="gap-2" data-testid="tab-policies">
            <Shield className="w-4 h-4" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
            <Users className="w-4 h-4" />
            Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="teams" className="gap-2" data-testid="tab-teams">
            <Building2 className="w-4 h-4" />
            Teams ({teams.length})
          </TabsTrigger>
          <TabsTrigger value="agents" className="gap-2" data-testid="tab-agents-in-team">
            <UserPlus className="w-4 h-4" />
            Agents ({unifiedAgents.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Roles & Permissions</CardTitle>
                <CardDescription>Configure access levels and permissions for each role</CardDescription>
              </div>
              <Button onClick={openCreateRole} data-testid="create-role-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Role Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead className="w-[70px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{roleRows}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Policies Tab — shows the module_permissions attached to each Role
            so admins can audit what each role can do. Editing the actual
            permissions lives inside the Role edit dialog (P2 keeps this a
            read-only auditor; P3+ can add inline editing here). */}
        <TabsContent value="policies" className="space-y-4" data-testid="policies-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>Policies</CardTitle>
              <CardDescription>
                Policies are permission sets attached to Roles. Users inherit permissions through their assigned Role. Edit a Role to change its policies.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {roles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8" data-testid="policies-empty">
                  No roles yet — create a Role in the Roles tab first.
                </p>
              ) : (
                roles.map((role) => {
                  const mp = role.module_permissions || {};
                  const modules = Object.keys(mp);
                  return (
                    <div key={role.id} className="border rounded-lg p-4" data-testid={`policy-row-${role.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="font-semibold text-[#1A2A4F] dark:text-slate-100 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-[#F5A623]" /> {role.name}
                            {role.is_system && <Badge variant="outline" className="text-xs">System</Badge>}
                          </div>
                          {role.description && <div className="text-xs text-muted-foreground mt-1">{role.description}</div>}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => openEditRole(role)} data-testid={`policy-edit-${role.id}`}>
                          <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                        </Button>
                      </div>
                      {modules.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">No policies attached — role has no module permissions.</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {modules.map((mod) => (
                            <div key={mod} className="text-xs">
                              <div className="font-medium capitalize text-slate-700 dark:text-slate-200">{mod.replace(/_/g, ' ')}</div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {(mp[mod] || []).map((action) => (
                                  <Badge key={action} variant="secondary" className="text-[10px] capitalize">{action}</Badge>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Team Members</CardTitle>
                <CardDescription>Manage your team and their office assignments</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {selectedUsers.length > 0 && (
                  <>
                    <span className="text-sm text-muted-foreground">{selectedUsers.length} selected</span>
                    <Button variant="outline" size="sm" onClick={() => setSelectedUsers([])}>
                      Clear
                    </Button>
                    <Button variant="destructive" size="sm" onClick={handleBulkDeleteUsers}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Selected
                    </Button>
                  </>
                )}
                <Button onClick={openCreateUser} data-testid="create-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={users.length > 0 && selectedUsers.length === users.length}
                        onCheckedChange={handleSelectAllUsers}
                      />
                    </TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Office</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[70px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{userRows}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Teams</CardTitle>
                <CardDescription>Organize team members into groups</CardDescription>
              </div>
              <Button onClick={openCreateTeam} data-testid="create-team-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Team
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team Name</TableHead>
                    <TableHead>Team Admin</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Universities</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead className="w-[70px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map(team => (
                    <TableRow key={team.id} data-testid={`team-row-${team.id}`}>
                      <TableCell className="font-medium">
                        {team.name}
                        {team.description && <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[220px]">{team.description}</div>}
                      </TableCell>
                      <TableCell>
                        {team.admin_name ? (
                          <Badge variant="outline" data-testid={`team-admin-badge-${team.id}`}>{team.admin_name}</Badge>
                        ) : (
                          <span className="text-xs text-amber-600">— Not assigned —</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={team.is_active ? 'default' : 'secondary'} data-testid={`team-status-${team.id}`}>
                          {team.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[250px]">
                          {team.university_names && team.university_names.length > 0 ? (
                            team.university_names.slice(0, 2).map((name, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">{name}</Badge>
                            ))
                          ) : (
                            <span className="text-muted-foreground text-sm">All universities</span>
                          )}
                          {team.university_names && team.university_names.length > 2 && (
                            <Badge variant="secondary" className="text-xs">+{team.university_names.length - 2} more</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{team.member_count || 0} members</Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditTeam(team)}>
                              <PenLine className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDeleteTeam(team)} className="text-destructive">
                              <Trash className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {teams.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No teams created yet. Create your first team to organize members.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agents tab — unified list from both db.agents + db.users(user_type=agent).
            Local Admin uses this to attach any agent to a Team and a Counselor
            within that team so future applications route correctly. */}
        <TabsContent value="agents" className="space-y-4" data-testid="agents-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>Agents in this Workspace</CardTitle>
              <CardDescription>
                Every agent — whether admin-created or public-signup + approved. Attach each agent to a Team and pick which Counselor will handle their applications.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Assigned Counselor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unifiedAgents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="agents-empty">
                        No agents yet. Add an agent from the Agents menu or approve a public signup.
                      </TableCell>
                    </TableRow>
                  ) : (
                    unifiedAgents.map((a) => (
                      <TableRow key={a.id} data-testid={`unified-agent-row-${a.id}`}>
                        <TableCell>
                          <div className="font-medium text-[#1A2A4F] dark:text-slate-100">{a.name || '—'}</div>
                          <div className="text-xs text-muted-foreground">{a.email}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {a.source === 'db.agents' ? 'Admin-added' : 'Public signup'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {a.team_name ? (
                            <Badge variant="secondary">{a.team_name}</Badge>
                          ) : (
                            <span className="text-xs text-amber-600">— Unassigned —</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {a.counselor_name ? (
                            <span className="text-sm">{a.counselor_name}</span>
                          ) : (
                            <span className="text-xs text-amber-600">— Unassigned —</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={a.is_active ? 'default' : 'secondary'} className="text-xs capitalize">
                            {a.approval_status === 'pending_approval' ? 'Pending' : (a.is_active ? 'Active' : 'Inactive')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAssignAgentDialog({ open: true, agent: a, team_id: a.team_id || '', counselor_id: a.assigned_counselor_id || '' })}
                            data-testid={`assign-agent-btn-${a.id}`}
                          >
                            <PenLine className="w-3.5 h-3.5 mr-1" /> Assign
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Agent Assignment Dialog — Team dropdown + counselor dropdown that
          re-filters based on the chosen team so admins can never create
          a mismatched Team/Counselor pair. */}
      <Dialog
        open={assignAgentDialog.open}
        onOpenChange={(open) => setAssignAgentDialog((d) => ({ ...d, open }))}
      >
        <DialogContent className="max-w-md" data-testid="assign-agent-dialog">
          <DialogHeader>
            <DialogTitle>Assign Agent</DialogTitle>
            <DialogDescription>
              Attach <span className="font-semibold">{assignAgentDialog.agent?.name}</span> to a Team and pick the Counselor who will handle their applications.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Team</Label>
              <Select
                value={assignAgentDialog.team_id || 'none'}
                onValueChange={(v) => setAssignAgentDialog((d) => ({ ...d, team_id: v === 'none' ? '' : v, counselor_id: '' }))}
              >
                <SelectTrigger data-testid="assign-agent-team-select">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassigned —</SelectItem>
                  {teams.filter((t) => t.is_active !== false).map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`assign-team-opt-${t.id}`}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assigned Counselor</Label>
              <p className="text-xs text-muted-foreground">Only counselors that belong to the selected team appear here.</p>
              <Select
                value={assignAgentDialog.counselor_id || 'none'}
                onValueChange={(v) => setAssignAgentDialog((d) => ({ ...d, counselor_id: v === 'none' ? '' : v }))}
                disabled={!assignAgentDialog.team_id}
              >
                <SelectTrigger data-testid="assign-agent-counselor-select">
                  <SelectValue placeholder={assignAgentDialog.team_id ? 'Select a counselor' : 'Pick a team first'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {users.filter((u) => u.team_id === assignAgentDialog.team_id).map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`assign-counselor-opt-${u.id}`}>
                      {u.name} — {u.email}
                    </SelectItem>
                  ))}
                  {assignAgentDialog.team_id && users.filter((u) => u.team_id === assignAgentDialog.team_id).length === 0 && (
                    <SelectItem value="__empty__" disabled>No counselors in this team yet</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignAgentDialog({ open: false, agent: null, team_id: '', counselor_id: '' })}>Cancel</Button>
            <Button
              onClick={async () => {
                try {
                  await api().put(`/agents/unified/${assignAgentDialog.agent.id}/assignment`, {
                    team_id: assignAgentDialog.team_id || null,
                    assigned_counselor_id: assignAgentDialog.counselor_id || null,
                  });
                  toast.success('Agent assignment updated');
                  setAssignAgentDialog({ open: false, agent: null, team_id: '', counselor_id: '' });
                  fetchData();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Failed to update assignment');
                }
              }}
              data-testid="assign-agent-save-btn"
            >
              Save assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Dialog */}
      <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTeam ? 'Edit Team' : 'Create Team'}</DialogTitle>
            <DialogDescription>
              {editingTeam ? 'Update team details and university assignments' : 'Create a new team and assign universities for lead distribution'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="teamName">Team Name *</Label>
              <Input
                id="teamName"
                value={teamFormData.name}
                onChange={(e) => setTeamFormData({ ...teamFormData, name: e.target.value })}
                placeholder="e.g. Sales Team, Admissions"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="teamDesc">Description</Label>
              <Input
                id="teamDesc"
                value={teamFormData.description}
                onChange={(e) => setTeamFormData({ ...teamFormData, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>

            {/* Status */}
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div>
                <Label className="text-sm">Status</Label>
                <p className="text-xs text-muted-foreground">Inactive teams keep data but are hidden from pickers.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium ${teamFormData.is_active ? 'text-emerald-700' : 'text-slate-500'}`}>
                  {teamFormData.is_active ? 'Active' : 'Inactive'}
                </span>
                <Switch
                  checked={teamFormData.is_active}
                  onCheckedChange={(v) => setTeamFormData({ ...teamFormData, is_active: v })}
                  data-testid="team-active-switch"
                />
              </div>
            </div>

            {/* Team Admin — dropdown filtered to users who hold the "Team Admin" role */}
            <div className="space-y-2">
              <Label>Team Admin</Label>
              <p className="text-xs text-muted-foreground">
                Only users with the <span className="font-semibold">Team Admin</span> role appear here. Create a user with that role first if the list is empty.
              </p>
              <Select
                value={teamFormData.admin_id || 'none'}
                onValueChange={(v) => setTeamFormData({ ...teamFormData, admin_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="team-admin-select">
                  <SelectValue placeholder="Select a Team Admin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No Team Admin —</SelectItem>
                  {(() => {
                    const teamAdminRoleIds = roles
                      .filter((r) => (r.name || '').toLowerCase() === 'team admin')
                      .map((r) => r.id);
                    const candidates = users.filter((u) => (u.role_ids || []).some((rid) => teamAdminRoleIds.includes(rid)));
                    return candidates.length === 0 ? (
                      <SelectItem value="__empty__" disabled data-testid="team-admin-empty-option">
                        No users with Team Admin role yet
                      </SelectItem>
                    ) : (
                      candidates.map((u) => (
                        <SelectItem key={u.id} value={u.id} data-testid={`team-admin-opt-${u.id}`}>
                          {u.name} — {u.email}
                        </SelectItem>
                      ))
                    );
                  })()}
                </SelectContent>
              </Select>
            </div>

            {/* Team Members — multi-select from existing users only (no duplication) */}
            <div className="space-y-2">
              <Label>Team Members</Label>
              <p className="text-xs text-muted-foreground">
                Add existing workspace users to this team. Users keep the Role they already have — nothing is recreated.
              </p>
              <ScrollArea className="h-[160px] border rounded-lg p-3" data-testid="team-members-picker">
                <div className="space-y-1.5">
                  {users.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No users to add — create a User first.</p>
                  ) : (
                    users.map((u) => {
                      const isAdmin = u.id === teamFormData.admin_id;
                      const inOther = u.team_id && editingTeam ? u.team_id !== editingTeam.id : !!u.team_id;
                      return (
                        <div key={u.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`member-${u.id}`}
                            checked={teamFormData.member_ids.includes(u.id) || isAdmin}
                            disabled={isAdmin}
                            onCheckedChange={(checked) => {
                              setTeamFormData((prev) => ({
                                ...prev,
                                member_ids: checked
                                  ? Array.from(new Set([...prev.member_ids, u.id]))
                                  : prev.member_ids.filter((id) => id !== u.id),
                              }));
                            }}
                            data-testid={`team-member-checkbox-${u.id}`}
                          />
                          <Label htmlFor={`member-${u.id}`} className="text-sm cursor-pointer flex-1">
                            {u.name} <span className="text-muted-foreground">— {u.email}</span>
                            {isAdmin && <Badge variant="outline" className="text-[10px] ml-2">Admin</Badge>}
                            {inOther && !isAdmin && <span className="text-xs text-amber-600 ml-2">(currently in {u.team_name || 'another team'})</span>}
                          </Label>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>

            <div className="space-y-2">
              <Label>Universities this team handles</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Select universities for lead distribution. Leave empty to handle all universities.
              </p>
              <ScrollArea className="h-[200px] border rounded-lg p-3">
                <div className="space-y-2">
                  {universities.length > 0 ? universities.map(uni => (
                    <div key={uni.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`uni-${uni.id}`}
                        checked={teamFormData.university_ids.includes(uni.id)}
                        onCheckedChange={() => toggleUniversitySelection(uni.id)}
                      />
                      <Label htmlFor={`uni-${uni.id}`} className="text-sm cursor-pointer flex-1">
                        {uni.name}
                      </Label>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No universities found. Add universities in Core Settings first.
                    </p>
                  )}
                </div>
              </ScrollArea>
              {teamFormData.university_ids.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {teamFormData.university_ids.length} university(ies) selected
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTeam}>
              {editingTeam ? 'Save Changes' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add Team Member'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user details and permissions' : 'Add a new member to your team'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={userFormData.name}
                onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                placeholder="John Doe"
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                Password {editingUser ? '(leave blank to keep current)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                placeholder="••••••••"
                data-testid="user-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Office Assignment *</Label>
              <Select
                value={userFormData.office_id || 'none'}
                onValueChange={(v) => setUserFormData({ ...userFormData, office_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="user-office-select">
                  <SelectValue placeholder="Select office" />
                </SelectTrigger>
                <SelectContent>{officeOptions}</SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Team Assignment</Label>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="noTeam"
                    checked={userFormData.no_team}
                    onCheckedChange={(checked) => setUserFormData({ 
                      ...userFormData, 
                      no_team: checked,
                      team_id: checked ? '' : userFormData.team_id 
                    })}
                  />
                  <Label htmlFor="noTeam" className="text-sm text-muted-foreground cursor-pointer">
                    Not attached to any team
                  </Label>
                </div>
                {!userFormData.no_team && (
                  <Select
                    value={userFormData.team_id || 'none'}
                    onValueChange={(v) => setUserFormData({ ...userFormData, team_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="user-team-select">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No team selected</SelectItem>
                      {teams.map(team => (
                        <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Roles *</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                {roleCheckboxes}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveUser} data-testid="save-user-btn">
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
            <DialogDescription>
              Configure navigation visibility and module permissions
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="roleName">Role Name *</Label>
                  <Input
                    id="roleName"
                    value={roleFormData.name}
                    onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })}
                    placeholder="e.g., Senior Counselor"
                    disabled={editingRole?.is_system && editingRole?.name !== 'Admin'}
                    data-testid="role-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="roleDesc">Description</Label>
                  <Input
                    id="roleDesc"
                    value={roleFormData.description}
                    onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                    placeholder="Role description"
                  />
                </div>
              </div>

              {/* Navigation Visibility */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Navigation Visibility</Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAllNavPermissions}>
                      Select All
                    </Button>
                    <Button variant="outline" size="sm" onClick={clearAllNavPermissions}>
                      Clear All
                    </Button>
                  </div>
                </div>
                <div className="border rounded-lg p-3 grid grid-cols-2 gap-1">
                  {navPermissionItems}
                </div>
              </div>

              {/* Module Permissions */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Module Permissions (CRUD)</Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAllModulePermissions}>
                      Select All
                    </Button>
                    <Button variant="outline" size="sm" onClick={clearAllModulePermissions}>
                      Clear All
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {modulePermissionSections}
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveRole} data-testid="save-role-btn">
              {editingRole ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
